// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const PROVINCES = [
  'AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT',
];

const PROPERTY_ID_TYPES = [
  'Assessment Roll #', 'PIN/PID', 'LINC #', 'Other',
];

const ASSESSMENT_PHASES = [
  'Assessment Date', 'Assessment Phase-in Date',
];

const TAX_YEARS = ['2022', '2023', '2024', '2025', '2026'];

const EXISTING_USE_OPTIONS = [
  'Residential Single Family',
  'Multiresidential',
  'Residential Condo/Strata',
  'Residential Other (specify)',
];

const OTHER_USES_OPTIONS = [
  'Agricultural', 'AirBNB', 'Carriage/Coach House', 'Group Home',
  'Hobby Farm', 'Live/Work', 'None', 'Other', 'Recreation Only',
  'Rental Only', 'Rooming House', 'Student Rental',
];

const OCCUPIED_BY_OPTIONS = [
  'Name', 'Not Applicable', 'Other', 'Owner', 'Owner and Tenant',
  'Owner and Tenants', 'Owner Up/Tenant Down', 'Owners',
  'Tenant', 'Tenants', 'Unknown', 'Vacant',
];

const PROPERTY_RIGHTS_OTHER = [
  'Cooperative', 'Co-ownership', 'First Nations Land',
  'Fractional Interest', 'Government Land', 'Parcel of Tied Land',
  'Partial Holding', 'Physical Segment', 'Other (specify)',
];

const NAME_TYPES = [
  'Applicant', 'Owner', 'Purchaser', 'Seller', 'Tenant', 'Unknown', 'Other',
];

const INTENDED_USE_OPTIONS = [
  'Conventional first mortgage financing',
  'Conventional second mortgage financing',
  'Mortgage insurance only',
  'Estate',
  'Marketing purposes',
  'Matrimonial',
  'Foreclosure/Power of Sale',
  'DRAFT Report Only',
  'Other (specify)',
];

const INTENDED_USE_ADD = [
  'Not Applicable',
  'Financing not to exceed 80% LTV',
  'Other (specify)',
];

const EXTRAORDINARY_OPTIONS = [
  '10 acres only', '5 acres only', 'As if 100% Complete', 'Other (specify)',
];

const INSPECTION_TYPES = [
  'Full Inspection Exterior and Interior',
  'Limited Inspection - Exterior only',
  'Limited Inspection - from street',
  'Other (specify)',
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function Field({ label, required, hint, children, style }) {
  return (
    <div className="rt-field" style={style}>
      <label>
        {label}
        {required && <span className="rt-required"> *</span>}
        {hint && <span className="rt-hint"> — {hint}</span>}
      </label>
      {children}
    </div>
  );
}

// ─── COMPONENT ───────────────────────────────────────────────────────────────

export default function SubjectTab({ meta, subject, onMetaChange, onSubjectChange }) {
  const setM = (k, v) => onMetaChange({ ...meta, [k]: v });
  const setS = (k, v) => onSubjectChange({ ...subject, [k]: v });
  const setA = (k, v) => onMetaChange({ ...meta, approaches_used: { ...meta.approaches_used, [k]: v } });

  const rightsOther = subject.property_rights_other_type ?? '';
  const hasOtherRight = subject.rights_fee_simple === false
    && subject.rights_condo === false
    && subject.rights_leasehold === false;

  return (
    <div className="rt-body">

      {/* ══ SUBJECT PROPERTY ══ */}
      <div className="rt-section">
        <h3 className="rt-section-title">Subject Property</h3>

        {/* Address block — read-only, sourced from workfile */}
        <div className="rt-row">
          <Field label="Property Address" hint="from workfile" style={{ flex: 2 }}>
            <div className="rt-display">{subject.address || '—'}</div>
          </Field>
          <Field label="City">
            <input type="text" value={subject.city ?? ''}
              onChange={e => setS('city', e.target.value)} />
          </Field>
        </div>

        <div className="rt-row">
          <Field label="Province">
            <select value={subject.province ?? ''}
              onChange={e => setS('province', e.target.value)}>
              <option value="">Select…</option>
              {PROVINCES.map(p => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Postal Code">
            <input type="text" value={subject.postal_code ?? ''}
              onChange={e => setS('postal_code', e.target.value)} />
          </Field>
          <Field label="Municipality & District" style={{ flex: 2 }}>
            <input type="text" value={subject.municipality ?? ''}
              onChange={e => setS('municipality', e.target.value)} />
          </Field>
        </div>

        <Field label="Legal Description">
          <textarea rows={2} value={subject.legal_description ?? ''}
            onChange={e => setS('legal_description', e.target.value)} />
        </Field>

        {/* Property ID */}
        <div className="rt-row">
          <Field label="Property ID Type">
            <select value={subject.property_id_type ?? ''}
              onChange={e => setS('property_id_type', e.target.value)}>
              <option value="">Select…</option>
              {PROPERTY_ID_TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Property ID #" style={{ flex: 2 }}>
            <input type="text" value={subject.property_id ?? ''}
              onChange={e => setS('property_id', e.target.value)} />
          </Field>
        </div>

        {/* Assessment */}
        <div className="rt-row">
          <Field label="Assessment $">
            <div className="rt-prefix-input">
              <span className="rt-prefix">$</span>
              <input type="text" value={subject.assessed_value ?? ''}
                onChange={e => setS('assessed_value', e.target.value)} />
            </div>
          </Field>
          <Field label="Assessment Phase">
            <select value={subject.assessment_phase ?? ''}
              onChange={e => setS('assessment_phase', e.target.value)}>
              <option value="">Select…</option>
              {ASSESSMENT_PHASES.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Assessment Date">
            <input type="date" value={subject.assessment_date || ''}
              onChange={e => setS('assessment_date', e.target.value)} />
          </Field>
        </div>

        <div className="rt-row">
          <Field label="Tax Year">
            <select value={subject.tax_year ?? ''}
              onChange={e => setS('tax_year', e.target.value)}>
              <option value="">Select…</option>
              {TAX_YEARS.map(y => <option key={y}>{y}</option>)}
            </select>
          </Field>
          <Field label="Taxes $">
            <div className="rt-prefix-input">
              <span className="rt-prefix">$</span>
              <input type="text" value={subject.taxes ?? ''}
                onChange={e => setS('taxes', e.target.value)} />
            </div>
          </Field>
        </div>

        {/* Use / Occupancy */}
        <div className="rt-row">
          <Field label="Existing Use">
            <select value={subject.existing_use ?? ''}
              onChange={e => setS('existing_use', e.target.value)}>
              <option value="">Select…</option>
              {EXISTING_USE_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Other Uses">
            <select value={subject.other_uses ?? ''}
              onChange={e => setS('other_uses', e.target.value)}>
              <option value="">Select…</option>
              {OTHER_USES_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Occupied By">
            <select value={subject.occupied_by ?? ''}
              onChange={e => setS('occupied_by', e.target.value)}>
              <option value="">Select…</option>
              {OCCUPIED_BY_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
        </div>

        {/* Property Rights */}
        <Field label="Property Rights">
          <div className="rt-check-group" style={{ marginTop: 4 }}>
            {[
              ['rights_fee_simple', 'Fee Simple'],
              ['rights_leasehold',  'Leasehold'],
              ['rights_condo',      'Condo/Strata'],
            ].map(([key, label]) => (
              <label key={key} className="rt-check">
                <input type="checkbox" checked={subject[key] ?? false}
                  onChange={e => setS(key, e.target.checked)} />
                {label}
              </label>
            ))}
            <label className="rt-check">
              <input type="checkbox" checked={subject.rights_other ?? false}
                onChange={e => setS('rights_other', e.target.checked)} />
              Other
            </label>
          </div>
          {(subject.rights_other || hasOtherRight) && (
            <select style={{ marginTop: 8 }} value={rightsOther}
              onChange={e => setS('property_rights_other_type', e.target.value)}>
              <option value="">Select type…</option>
              {PROPERTY_RIGHTS_OTHER.map(o => <option key={o}>{o}</option>)}
            </select>
          )}
        </Field>

        {/* Maintenance / Condo */}
        <div className="rt-row">
          <Field label="Maintenance Fee $">
            <div className="rt-prefix-input">
              <span className="rt-prefix">$</span>
              <input type="text" value={subject.maintenance_fee ?? ''}
                onChange={e => setS('maintenance_fee', e.target.value)} />
            </div>
          </Field>
          <Field label="Period">
            <div className="rt-check-group" style={{ marginTop: 8 }}>
              {['Monthly', 'Annual'].map(p => (
                <label key={p} className="rt-check">
                  <input type="radio"
                    checked={subject.maintenance_period === p}
                    onChange={() => setS('maintenance_period', p)} />
                  {p}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Source">
            <input type="text" value={subject.maintenance_source ?? ''}
              onChange={e => setS('maintenance_source', e.target.value)} />
          </Field>
        </div>

        <Field label="Special Assessment / Levies">
          <input type="text" value={subject.special_assessment ?? ''}
            onChange={e => setS('special_assessment', e.target.value)} />
        </Field>

        <Field label="Condo / Strata Complex Name">
          <input type="text" value={subject.condo_name ?? ''}
            onChange={e => setS('condo_name', e.target.value)} />
        </Field>
      </div>

      {/* ══ ASSIGNMENT ══ */}
      <div className="rt-section">
        <h3 className="rt-section-title">Assignment</h3>

        {/* Applicant */}
        <div className="rt-row">
          <Field label="Applicant / Borrower Name" required style={{ flex: 2 }}>
            <input type="text" value={subject.applicant_name ?? ''}
              onChange={e => setS('applicant_name', e.target.value)} />
          </Field>
          <Field label="Name Type">
            <select value={subject.applicant_name_type ?? ''}
              onChange={e => setS('applicant_name_type', e.target.value)}>
              <option value="">Select…</option>
              {NAME_TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
        </div>

        {/* Purpose */}
        <Field label="Purpose">
          <div className="rt-check-group" style={{ marginTop: 4 }}>
            {[
              ['purpose_mv',   'To Estimate Market Value'],
              ['purpose_mr',   'To Estimate Market Rent'],
            ].map(([key, label]) => (
              <label key={key} className="rt-check">
                <input type="checkbox" checked={meta[key] ?? false}
                  onChange={e => setM(key, e.target.checked)} />
                {label}
              </label>
            ))}
            <label className="rt-check">
              <input type="checkbox" checked={meta.purpose_other ?? false}
                onChange={e => setM('purpose_other', e.target.checked)} />
              Other
            </label>
          </div>
          {meta.purpose_other && (
            <input type="text" style={{ marginTop: 8 }}
              placeholder="Specify purpose…"
              value={meta.purpose_other_text ?? ''}
              onChange={e => setM('purpose_other_text', e.target.value)} />
          )}
        </Field>

        {/* Authorized Use */}
        <div className="rt-row">
          <Field label="Authorized Use" required style={{ flex: 2 }}>
            <select value={meta.intended_use ?? ''}
              onChange={e => setM('intended_use', e.target.value)}>
              <option value="">Select…</option>
              {INTENDED_USE_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Additional">
            <select value={meta.intended_use_add ?? ''}
              onChange={e => setM('intended_use_add', e.target.value)}>
              <option value="">Select…</option>
              {INTENDED_USE_ADD.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
        </div>
        {meta.intended_use === 'Other (specify)' && (
          <Field label="Specify Authorized Use">
            <input type="text" value={meta.intended_use_other ?? ''}
              onChange={e => setM('intended_use_other', e.target.value)} />
          </Field>
        )}

        <Field label="Authorized Users (by name)" hint="auto-filled with client name">
          <textarea rows={2} value={meta.intended_user ?? ''}
            onChange={e => setM('intended_user', e.target.value)} />
        </Field>

        {/* Requested By */}
        <Field label="Requested By">
          <div className="rt-check-group" style={{ marginTop: 4 }}>
            <label className="rt-check">
              <input type="radio" checked={meta.requested_by === 'client'}
                onChange={() => setM('requested_by', 'client')} />
              Client Above
            </label>
            <label className="rt-check">
              <input type="radio" checked={meta.requested_by === 'other'}
                onChange={() => setM('requested_by', 'other')} />
              Other
            </label>
          </div>
          {meta.requested_by === 'other' && (
            <input type="text" style={{ marginTop: 8 }}
              value={meta.requested_by_other ?? ''}
              onChange={e => setM('requested_by_other', e.target.value)} />
          )}
        </Field>

        {/* Value */}
        <Field label="Value">
          <div className="rt-check-group" style={{ marginTop: 4 }}>
            <label className="rt-check">
              <input type="radio" checked={meta.value_type !== 'retrospective'}
                onChange={() => setM('value_type', 'current')} />
              Current
            </label>
            <label className="rt-check">
              <input type="radio" checked={meta.value_type === 'retrospective'}
                onChange={() => setM('value_type', 'retrospective')} />
              Retrospective
            </label>
          </div>
        </Field>

        {/* Update of Original Report */}
        <Field label="">
          <label className="rt-check">
            <input type="checkbox" checked={meta.is_update ?? false}
              onChange={e => setM('is_update', e.target.checked)} />
            Update of Original Report
          </label>
        </Field>
        {meta.is_update && (
          <div className="rt-row" style={{ marginTop: 8 }}>
            <Field label="Originally Completed On">
              <input type="date" value={meta.original_effective_date || ''}
                onChange={e => setM('original_effective_date', e.target.value)} />
            </Field>
            <Field label="With Effective Date Of">
              <input type="date" value={meta.original_effective_date || ''}
                onChange={e => setM('original_effective_date', e.target.value)} />
            </Field>
            <Field label="File No.">
              <input type="text" value={meta.original_file_number ?? ''}
                onChange={e => setM('original_file_number', e.target.value)} />
            </Field>
          </div>
        )}

        {/* Approaches Used */}
        <Field label="Approaches Used">
          <div className="rt-check-group" style={{ marginTop: 4 }}>
            {[
              ['dca',    'Direct Comparison Approach'],
              ['cost',   'Cost Approach'],
              ['income', 'Income Approach'],
            ].map(([key, label]) => (
              <label key={key} className="rt-check">
                <input type="checkbox"
                  checked={meta.approaches_used?.[key] ?? false}
                  onChange={e => setA(key, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>
        </Field>

        {/* Extraordinary Assumptions */}
        <div className="rt-row">
          <Field label="Extraordinary Assumptions">
            <div className="rt-check-group" style={{ marginTop: 4 }}>
              <label className="rt-check">
                <input type="radio" checked={!meta.has_extraordinary}
                  onChange={() => setM('has_extraordinary', false)} />
                No
              </label>
              <label className="rt-check">
                <input type="radio" checked={!!meta.has_extraordinary}
                  onChange={() => setM('has_extraordinary', true)} />
                Yes
              </label>
            </div>
            {meta.has_extraordinary && (
              <select style={{ marginTop: 8 }} value={meta.extraordinary_type ?? ''}
                onChange={e => setM('extraordinary_type', e.target.value)}>
                <option value="">Select type…</option>
                {EXTRAORDINARY_OPTIONS.map(o => <option key={o}>{o}</option>)}
              </select>
            )}
          </Field>

          {/* Hypothetical Conditions */}
          <Field label="Hypothetical Conditions">
            <div className="rt-check-group" style={{ marginTop: 4 }}>
              <label className="rt-check">
                <input type="radio" checked={!meta.has_hypothetical}
                  onChange={() => setM('has_hypothetical', false)} />
                No
              </label>
              <label className="rt-check">
                <input type="radio" checked={!!meta.has_hypothetical}
                  onChange={() => setM('has_hypothetical', true)} />
                Yes
              </label>
            </div>
            {meta.has_hypothetical && (
              <select style={{ marginTop: 8 }} value={meta.hypothetical_type ?? ''}
                onChange={e => setM('hypothetical_type', e.target.value)}>
                <option value="">Select type…</option>
                {EXTRAORDINARY_OPTIONS.map(o => <option key={o}>{o}</option>)}
              </select>
            )}
          </Field>
        </div>

        {/* Inspection */}
        <div className="rt-row">
          <Field label="Inspection Type" required style={{ flex: 2 }}>
            <select value={meta.inspection_type ?? ''}
              onChange={e => setM('inspection_type', e.target.value)}>
              <option value="">Select…</option>
              {INSPECTION_TYPES.map(o => <option key={o}>{o}</option>)}
            </select>
          </Field>
        </div>

        <div className="rt-row">
          <Field label="Inspection Date" required hint="auto-filled from workfile">
            <input type="date" value={meta.inspection_date || ''}
              onChange={e => setM('inspection_date', e.target.value)} />
          </Field>
          <Field label="Effective Date" required>
            <input type="date" value={meta.effective_date || ''}
              onChange={e => setM('effective_date', e.target.value)} />
          </Field>
          <Field label="Report Date" required>
            <input type="date" value={meta.report_date || ''}
              onChange={e => setM('report_date', e.target.value)} />
          </Field>
        </div>
      </div>

    </div>
  );
}
